export type SyncState = 'account' | 'pending' | 'local_only' | 'failed';

export interface SyncMetadata {
  sync_state?: SyncState;
  account_id?: string;
  local_id?: string;
  synced_at?: string;
  last_sync_attempt_at?: string;
  last_sync_error?: string | null;
}

export interface SyncStatusBreakdown {
  account: number;
  pending: number;
  local_only: number;
  failed: number;
}

export interface UserDataSyncSummary {
  favorites: SyncStatusBreakdown;
  reports: SyncStatusBreakdown;
  views: SyncStatusBreakdown;
  pending: number;
  local_only: number;
  failed: number;
  hasUnsyncedData: boolean;
}

export const EMPTY_SYNC_BREAKDOWN: SyncStatusBreakdown = {
  account: 0,
  pending: 0,
  local_only: 0,
  failed: 0,
};

/** Stable SSR + first client paint; real sync stats come from `userDataService` after mount. */
export const EMPTY_USER_DATA_SYNC_SUMMARY: UserDataSyncSummary = {
  favorites: { ...EMPTY_SYNC_BREAKDOWN },
  reports: { ...EMPTY_SYNC_BREAKDOWN },
  views: { ...EMPTY_SYNC_BREAKDOWN },
  pending: 0,
  local_only: 0,
  failed: 0,
  hasUnsyncedData: false,
};

export function normalizeSyncState(value: unknown, fallback: SyncState = 'local_only'): SyncState {
  if (value === 'account' || value === 'pending' || value === 'local_only' || value === 'failed') {
    return value;
  }
  return fallback;
}

export function syncStateLabel(state: SyncState): string {
  switch (state) {
    case 'account':
      return 'Saved to account';
    case 'pending':
      return 'Sync pending';
    case 'failed':
      return 'Sync failed';
    case 'local_only':
    default:
      return 'Saved on this device';
  }
}

export function summarizeSyncStates(items: SyncMetadata[]): SyncStatusBreakdown {
  return items.reduce<SyncStatusBreakdown>((summary, item) => {
    const state = normalizeSyncState(item.sync_state, item.account_id ? 'account' : 'local_only');
    summary[state] += 1;
    return summary;
  }, { ...EMPTY_SYNC_BREAKDOWN });
}

export function buildUserDataSyncSummary(data: {
  favorites: SyncMetadata[];
  reports: SyncMetadata[];
  views: SyncMetadata[];
}): UserDataSyncSummary {
  const favorites = summarizeSyncStates(data.favorites);
  const reports = summarizeSyncStates(data.reports);
  const views = summarizeSyncStates(data.views);
  const pending = favorites.pending + reports.pending + views.pending;
  const local_only = favorites.local_only + reports.local_only + views.local_only;
  const failed = favorites.failed + reports.failed + views.failed;

  return {
    favorites,
    reports,
    views,
    pending,
    local_only,
    failed,
    hasUnsyncedData: pending > 0 || local_only > 0 || failed > 0,
  };
}
